import { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';

import { Text, Types, Image, Button, Heading, LogoContainer } from '@marriott/mi-ui-library';
// import { Badge } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { useSession } from '@marriott/mi-headless-utils';

import config from '../../lib/config';
import {
  CLASSIC_ASPECT_RATIO,
  EXPLORE_THIS_OFFER,
  MPO_SBO_BUTTON,
  SINGLE_BRAND_MULTI_PROP_OFFER,
  COBRAND_SINGLE_OFFER,
} from '../../constants/OffersHeroConstants';
import {
  IMAGE_CACHE_DOMAIN,
  IMAGE_CACHE_DOMAIN_CN,
  INTERNAL_CTA_TYPE,
  EXTERNAL_CTA_TYPE,
} from '../../constants/CommonConstants';
import { getFallbackImg, constructCobrandCTAUrl } from '../../utils/OfferUtils';
import { formCustomClickTrackValue } from '../../utils/CommonUtils';

import { OffersHeroMultiPropertyProps } from './OffersHero.types';
import { OffersHeroMultiPropertyStyled } from './OffersHero.styles';

export const OffersHeroMultiProperty: React.FC<OffersHeroMultiPropertyProps> = props => {
  const { variation, offersHeroProps, modelObj, getOfferDataFromLightning, isLogoFlagEnabled } = props;
  const isCobrandVariation = variation === COBRAND_SINGLE_OFFER;
  const offerObj =
    getOfferDataFromLightning || isCobrandVariation
      ? offersHeroProps?.responseObject?.offers?.[0]
      : offersHeroProps?.responseObject?.edges?.[0]?.node;
  const { envVarsObject } = useClientEnvVarsStore();
  const { datalayer } = useSession();
  const imageDomain = envVarsObject?.['UXL_IMAGE_CACHE_DOMAIN'] ?? IMAGE_CACHE_DOMAIN;
  const BAIDU_MAP_API_KEY = envVarsObject?.['BAIDU_MAP_API_KEY'];
  const isChinaDomain = imageDomain === IMAGE_CACHE_DOMAIN_CN;
  let desktopImageSrc = '';
  const fallBackImg = getFallbackImg(
    isChinaDomain ? config.FALLBACK_CN_IMG_URI : config.FALLBACK_IMG_URI,
    CLASSIC_ASPECT_RATIO
  );
  const bannerTextClassName = clsx('t-font-swiss721');
  const OffersHeroMPORef = useRef(null);
  const ASPECT_RATIO_UXL_FIELD_MAPPING = {
    CLASSIC: 'classicHorizontal',
  };
  const objKey = CLASSIC_ASPECT_RATIO as keyof typeof ASPECT_RATIO_UXL_FIELD_MAPPING;
  const imageUrls = offerObj?.media?.primaryImage?.imageUrls;
  const DACImagePath = imageUrls?.[ASPECT_RATIO_UXL_FIELD_MAPPING?.[objKey]];
  if (DACImagePath) {
    desktopImageSrc = imageDomain + DACImagePath;
  } else {
    desktopImageSrc = imageDomain + fallBackImg;
  }

  const logoImageSrc = offerObj?.media?.secondaryImage?.imageSrc
    ? imageDomain + offerObj.media.secondaryImage.imageSrc
    : '';

  const buttonLabel = isCobrandVariation ? offerObj?.ctaLabel : modelObj?.exploreThisOffer || EXPLORE_THIS_OFFER;

  // Use string type for openInNewtab for compatibility with formCustomClickTrackValue
  const openInNewtab: 'External' | 'Internal' = isCobrandVariation && offerObj?.ctaExtInd ? 'External' : 'Internal';

  // Clone trackingProperties to avoid direct mutation
  const trackingProperties = { ...modelObj?.trackingProperties };
  if (isCobrandVariation) {
    Object.assign(trackingProperties, {
      trackingDescription: offerObj?.trackingValue,
      location: buttonLabel,
    });
  }
  const customClickTrackValue = isCobrandVariation
    ? formCustomClickTrackValue(trackingProperties, openInNewtab)
    : `${modelObj?.id}|${buttonLabel}|${openInNewtab}`;

  const handleExploreOffer = (): void => {
    const targetSection = document.getElementById('offers-search-form')?.offsetTop;
    const isFixedHeader = document.body.classList.contains('fixed-header');
    window.scrollTo({
      top: targetSection ? (isFixedHeader ? targetSection - 50 : targetSection - 150) : 0,
      behavior: 'smooth',
    });
  };
  const [ctaUrl, setCtaUrl] = useState('');

  useEffect(() => {
    // Only run on client
    if (typeof window !== 'undefined') {
      const url = constructCobrandCTAUrl(offerObj, datalayer) || '';
      setCtaUrl(url);
    }
  }, [offerObj, datalayer, variation]);

  return (
    <OffersHeroMultiPropertyStyled className="brand-css">
      <div
        className={`section-wrapper ${
          variation === SINGLE_BRAND_MULTI_PROP_OFFER
            ? modelObj?.styleclass ?? `color-scheme7`
            : variation === COBRAND_SINGLE_OFFER
            ? 'cobrand-single-offer-wrapper'
            : ''
        }`}
      >
        <div className="image-section">
          <Image
            title={`${offerObj?.title} img`}
            customClass="hero-img"
            defaultImageURL={desktopImageSrc}
            altText={`${offerObj?.title} img`}
            loading="eager"
          ></Image>
        </div>
        <div
          className={`details-section ${
            variation === SINGLE_BRAND_MULTI_PROP_OFFER && 't-background-color-rgba'
          } ${variation}`}
        >
          <div className="details mpo">
            {variation === SINGLE_BRAND_MULTI_PROP_OFFER &&
              (isLogoFlagEnabled ? (
                <LogoContainer
                  brand={`brand-logo-${offersHeroProps?.brand}`}
                  width={160}
                  height={90}
                  aspectRatio="3:2"
                  alignment="Centered"
                  isLink={false}
                  color={'background-aware'}
                />
              ) : (
                <div className="logo t-brand-logo-s">
                  <div
                    className={`brand-image brand-logo-${offersHeroProps?.brand} hotel-brand-logo t-brand-logo`}
                  ></div>
                </div>
              ))}
            {isCobrandVariation && (
              <div className="credit-card-container">
                {/* Commenting Badge atom, can be used once offer data has related fields.
                 */}
                {/* <Badge badgeVariation={Types.BadgeVariation.Overlay} badgeText={'NO ANNUAL FEE'} badgeIcon={'icon-lock'} /> */}
                <Image
                  title={offerObj?.media?.secondaryImage.title}
                  customClass="logo-image"
                  loading="eager"
                  defaultImageURL={logoImageSrc}
                  altText={offerObj?.media?.secondaryImage?.alternateDescription}
                  dynamic={false}
                ></Image>
              </div>
            )}
            <div className="title">
              <Heading
                titleText={offerObj?.title}
                variation={Types.headingType.title}
                customClass={clsx('t-font-swiss721')}
                fontSize={Types.size.medium}
              />
            </div>
            <div className="desc">
              <Text
                copyText={offerObj?.descriptionTeaser}
                fontSize={Types.size.medium}
                customClass={clsx('t-font-swiss721')}
                element={Types.tags.paragraph}
              />
            </div>
            <div className={`navigation-btns ${variation === SINGLE_BRAND_MULTI_PROP_OFFER ? MPO_SBO_BUTTON : ''}`}>
              <Button
                {...(variation !== COBRAND_SINGLE_OFFER && {
                  callback: (): void => handleExploreOffer(),
                })}
                {...(isCobrandVariation && {
                  href: ctaUrl,
                  isLink: true,
                })}
                custom_click_track_value={customClickTrackValue}
                className={clsx(
                  `m-button-primary  ${bannerTextClassName} ${
                    isCobrandVariation && offerObj?.ctaExtInd ? 'm-button-external' : ''
                  }`
                )}
                target={isCobrandVariation && offerObj?.ctaExtInd ? '_blank' : '_self'}
                trackingProperties={modelObj?.trackingProperties}
                ref={OffersHeroMPORef}
              >
                {buttonLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </OffersHeroMultiPropertyStyled>
  );
};

export default OffersHeroMultiProperty;
